import { nombreTurno } from '../../utils/turnos';
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { guardarInstrucciones, listCuentasBancarias, crearCuentaBancaria, listCuentasProveedor, crearCuentaProveedor, getPendientesProveedor } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import { formatDateTime } from '../../utils/dates';
import { fechaCorta } from '../finanzas/estados';
import ProveedorAutocomplete from '../registrar-cierre/ProveedorAutocomplete';
import MoneyInput from '../common/MoneyInput';
import '../finanzas/ui.css';
import FondoModal from '../common/FondoModal';

export const TIPOS_INSTRUCCION = [['depositar', 'Depositar'], ['proveedor', 'Pagar proveedor'], ['servicio', 'Pagar servicio'],
  ['propietario', 'Entregar a persona'], ['otro', 'Otro']];
const nombreTipo = Object.fromEntries(TIPOS_INSTRUCCION);
const n = (v) => Number(v) || 0;
const round = (v) => Math.round(v * 100) / 100;
const moneyOk = (v) => /^\d*(?:\.\d{0,2})?$/.test(v);
const lineaVacia = () => ({ key: Math.random().toString(36).slice(2), id: null, tipo: 'depositar', destino: '', monto: '', banco: '', cuenta: '', referencia: '',
  factura: '', cuentaBancariaId: null, proveedorId: null, proveedorCuentaId: null, cuentaPorPagarId: null, conCuenta: false });

/** Texto para WhatsApp: se arma siempre desde las instrucciones guardadas, así refleja el último cambio.
 *  Los montos (físico, fondo, disponible, asignado, restante) vienen calculados del servidor (`resumen`). */
export function textoInstrucciones({ sucursal, cierre, resumen: r, instrucciones }) {
  const lineas = instrucciones.filter((i) => i.estado !== 'Anulada').map((i, k) => {
    const det = [i.factura && `Factura: ${i.factura}`, i.banco && `Banco: ${i.banco}`, i.cuenta && `Cuenta: ${i.cuenta}`,
      i.referencia && `${i.tipo === 'propietario' ? 'Obs.' : 'Ref./obs.'}: ${i.referencia}`].filter(Boolean);
    return [`${k + 1}. *${nombreTipo[i.tipo] || i.tipo}* — ${i.destino}: *${toMoney(i.monto)}*`, ...det.map((x) => `   ${x}`)].join('\n');
  });
  return [
    `*Instrucciones del efectivo*`,
    `${sucursal ? `${sucursal} · ` : ''}Cierre ${fechaCorta(cierre.fecha)} · ${nombreTurno(cierre.turnoId)}`,
    '',
    ...lineas,
    '',
    `${r.conteoFisico ? 'Efectivo contado' : 'Efectivo resultante'}: ${toMoney(r.efectivoFisico)}`,
    ...(r.fondoCaja > 0 ? [`Dejar en caja: ${toMoney(r.fondoCaja)}`] : []),
    `Disponible para otros destinos: ${toMoney(r.disponible)}`,
    `Total asignado: ${toMoney(r.asignado)}`,
    `Saldo restante: ${toMoney(r.sinAsignar)}`,
    '',
    'Si no puedes realizar alguna instrucción, avísame antes de hacer otra cosa con el dinero.',
  ].join('\n');
}

async function copiar(texto) {
  try { await navigator.clipboard.writeText(texto); }
  catch {
    // Respaldo para navegadores sin acceso al portapapeles (http o permisos).
    const t = document.createElement('textarea');
    t.value = texto; t.style.position = 'fixed'; t.style.opacity = '0';
    document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
  }
}

// Instrucciones al encargado sobre qué hacer con el efectivo del cierre. Guardadas y editables.
// `resumen` viene del servidor: misma regla que la liquidación (físico − fondo en caja = disponible).
export default function InstruccionesEfectivo({ cierre, sucursal, resumen: r, instrucciones, onSaved }) {
  const [editando, setEditando] = useState(false);
  const [abierta, setAbierta] = useState(null); // instrucción con el detalle visible
  const texto = instrucciones.length ? textoInstrucciones({ sucursal, cierre, resumen: r, instrucciones }) : '';

  const onCopiar = async () => {
    await copiar(texto);
    Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Instrucciones copiadas', showConfirmButton: false, timer: 1800 });
  };
  const onWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');

  // Lista compacta: qué indicó Administración. Los montos del efectivo están una sola vez en "Resumen del efectivo".
  return (
    <section className="cd-seccion">
      <div className="cd-seccion-hd">
        <h4>Instrucciones para el encargado</h4>
        <div className="cd-seccion-acc">
          {instrucciones.length ? <>
            <button type="button" className="fx-btn" onClick={() => setEditando(true)}>Editar instrucciones</button>
            <button type="button" className="fx-btn" onClick={onCopiar}>Copiar</button>
            <button type="button" className="fx-btn fx-btn--primary" onClick={onWhatsApp}>WhatsApp</button>
          </> : <button type="button" className="fx-btn fx-btn--primary" onClick={() => setEditando(true)} disabled={r.disponible <= 0}>Generar instrucciones</button>}
        </div>
      </div>
      {instrucciones.length ? <>
        <small className="cd-nota">Asignado {toMoney(r.asignado)} · Sin asignar <b style={{ color: r.sinAsignar > 0 ? 'var(--warn-text)' : undefined }}>{toMoney(r.sinAsignar)}</b></small>
        <ol className="cd-lista">{instrucciones.map((i, k) => (
          <li key={i.id} className={i.estado === 'Anulada' ? 'cd-anulada' : undefined}>
            <div className="cd-fila">
              <span className="cd-fila-tit"><b>{k + 1}. {i.tipoNombre} — {i.destino}</b>
                <small>{toMoney(i.monto)} · {i.estadoNombre || i.estado}{i.resultado ? ` → ${i.resultado.tipoNombre} ${toMoney(i.resultado.monto)}` : ''}</small></span>
              <span className="cd-fila-acc">
                <button type="button" className="fx-link" aria-expanded={abierta === i.id} onClick={() => setAbierta(abierta === i.id ? null : i.id)}>{abierta === i.id ? 'Ocultar' : 'Ver detalle'}</button>
                {/* Solo un indicador: los comprobantes se ven y se adjuntan en Liquidación (lo que realmente se hizo). */}
                <a className="cd-comp-ind" href={`#instr-${i.id}`} title="Ver en Liquidación" onClick={(e) => { e.preventDefault(); document.getElementById(`instr-${i.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
                  {(i.comprobantes || []).length} comprobante{(i.comprobantes || []).length === 1 ? '' : 's'}</a>
              </span>
            </div>
            {abierta === i.id && <small className="cd-detalle">{[i.factura && `Factura: ${i.factura}`, i.banco && `Banco: ${i.banco}`, i.cuenta && `Cuenta: ${i.cuenta}`,
              i.referencia && `Detalle de la instrucción: ${i.referencia}`, `Generada por ${i.creadoPor || '—'} · ${i.creadoAt ? formatDateTime(i.creadoAt) : ''}`].filter(Boolean).join(' · ')}</small>}
          </li>))}</ol>
        <details className="cd-wa">
          <summary>Ver mensaje para WhatsApp</summary>
          <pre>{texto}</pre>
        </details>
      </> : <p className="fx-empty cd-vacio">Aún no hay instrucciones. Genera las indicaciones para definir qué debe hacerse con el efectivo disponible.</p>}
      {editando && <EditorInstrucciones cierre={cierre} disponible={r.disponible} instrucciones={instrucciones} onClose={() => setEditando(false)}
        onSaved={async () => { setEditando(false); await onSaved?.(); }} />}
    </section>
  );
}

// Campos que usa cada tipo (el servidor también vacía los que no aplican).
const camposDeTipo = (l, tipo) => ({ ...l, tipo, destino: '', banco: '', cuenta: '', factura: '', cuentaBancariaId: null,
  proveedorId: null, proveedorCuentaId: null, cuentaPorPagarId: null, conCuenta: false });
const errorDeLinea = (l) => {
  if (!(n(l.monto) > 0)) return 'indica un monto mayor a 0';
  if (l.tipo === 'depositar' && !l.cuentaBancariaId) return 'elige la cuenta bancaria';
  if (l.tipo === 'proveedor' && !l.proveedorId) return 'elige el proveedor del catálogo';
  if (l.tipo === 'servicio' && !l.destino.trim()) return 'indica el servicio o beneficiario';
  if (l.tipo === 'servicio' && l.conCuenta && !l.cuenta.trim()) return 'indica la cuenta bancaria o desmarca "Se paga a una cuenta bancaria"';
  if (l.tipo === 'propietario' && !l.destino.trim()) return 'indica el nombre de la persona';
  if (l.tipo === 'otro' && !l.destino.trim()) return 'indica el destino o descripción';
  return '';
};

function EditorInstrucciones({ cierre, disponible, instrucciones, onClose, onSaved }) {
  const { sucursalesDisponibles: sucursales } = useSucursalActiva(); // solo la actual si hay una elegida
  // Solo las abiertas (Pendiente / No realizada) se editan. Las cerradas se muestran sin cambios; las aplicadas cuentan en el total
  // (las de cambio de destino por su monto aplicado). Quitar una abierta la deja Anulada en el historial.
  const realizadas = instrucciones.filter((i) => i.estado === 'Realizada' || i.estado === 'Cambio de destino');
  const montoRealizado = round(realizadas.reduce((s, i) => s + n(i.estado === 'Cambio de destino' ? i.resultado?.monto : i.monto), 0));
  const editables = instrucciones.filter((i) => i.estado === 'Pendiente' || i.estado === 'No realizada');
  const [lineas, setLineas] = useState(() => (editables.length
    ? editables.map((i) => ({ ...lineaVacia(), ...i, key: i.id, monto: String(i.monto), conCuenta: i.tipo === 'servicio' && !!i.cuenta }))
    : realizadas.length ? [] : [lineaVacia()]));
  const [cuentas, setCuentas] = useState([]);
  const [busy, setBusy] = useState(false);
  const total = round(lineas.reduce((s, l) => s + n(l.monto), 0) + montoRealizado);
  const restante = round(disponible - total); // vista previa mientras se edita; el servidor valida al guardar
  const excede = restante < -0.001;

  const cargarCuentas = useCallback(() => listCuentasBancarias(cierre.sucursalId).then(setCuentas).catch(() => setCuentas([])), [cierre.sucursalId]);
  useEffect(() => { cargarCuentas(); }, [cargarCuentas]);

  const cambiar = (key, cambios) => setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...cambios } : l)));
  const quitar = (key) => setLineas((ls) => ls.filter((l) => l.key !== key));

  const guardar = async (e) => {
    e.preventDefault();
    const malo = lineas.findIndex((l) => errorDeLinea(l));
    if (malo >= 0) return Swal.fire('Instrucciones', `Instrucción ${malo + 1}: ${errorDeLinea(lineas[malo])}.`, 'warning');
    if (excede) return Swal.fire('Instrucciones', 'El total asignado supera el efectivo disponible para otros destinos.', 'warning');
    if (!lineas.length && editables.length) {
      const { isConfirmed } = await Swal.fire({ title: 'Quitar instrucciones', text: 'Las instrucciones pendientes quedarán anuladas (se conservan en el historial).', icon: 'warning', showCancelButton: true, confirmButtonText: 'Quitar' });
      if (!isConfirmed) return;
    }
    setBusy(true);
    try {
      await guardarInstrucciones(cierre.id, lineas.map((l) => ({ id: l.id, tipo: l.tipo, destino: l.destino, monto: n(l.monto).toFixed(2), referencia: l.referencia,
        banco: l.tipo === 'servicio' && l.conCuenta ? l.banco : '', cuenta: l.tipo === 'servicio' && l.conCuenta ? l.cuenta : '',
        factura: l.factura, cuentaBancariaId: l.cuentaBancariaId, proveedorId: l.proveedorId, proveedorCuentaId: l.proveedorCuentaId, cuentaPorPagarId: l.cuentaPorPagarId })));
      await onSaved?.();
    } catch (err) { Swal.fire('No se pudo guardar', err.message || 'Intenta de nuevo.', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <FondoModal onClose={onClose} style={{ zIndex: 1300 }}>
      <form className="fx-modal" role="dialog" aria-modal="true" aria-label="Instrucciones del efectivo" onSubmit={guardar} onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>{instrucciones.length ? 'Editar instrucciones' : 'Generar instrucciones'}</h2>
          <p>Disponible para otros destinos: <b>{toMoney(disponible)}</b> · Asignado: <b>{toMoney(total)}</b> · Restante: <b style={{ color: excede ? 'var(--danger)' : undefined }}>{toMoney(restante)}</b></p></div>
          <button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>

        {realizadas.length > 0 && <div className="fx-card" style={{ marginBottom: 10, background: 'var(--ok-bg)', borderColor: 'var(--ok-border)' }}>
          <b style={{ fontSize: 14 }}>Ya cerradas (no se pueden editar)</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13 }}>
            {realizadas.map((i) => <li key={i.id}>{i.tipoNombre} · {i.destino}: <b>{toMoney(i.monto)}</b> — {i.estadoNombre}{i.resultado ? ` → ${i.resultado.tipoNombre} ${toMoney(i.resultado.monto)}` : ''} · {i.confirmadoPor || '—'}</li>)}
          </ul>
        </div>}
        {lineas.map((l, k) => (
          <LineaInstruccion key={l.key} l={l} numero={k + 1 + realizadas.length} sucursalId={cierre.sucursalId} sucursales={sucursales} cuentas={cuentas}
            onCuentaCreada={cargarCuentas} onChange={(c) => cambiar(l.key, c)} onQuitar={() => quitar(l.key)} />))}
        {!lineas.length && !realizadas.length && <p className="fx-empty">Sin instrucciones. Al guardar, las pendientes quedarán anuladas.</p>}
        <button type="button" className="fx-btn" onClick={() => setLineas((ls) => [...ls, { ...lineaVacia(), monto: restante > 0 ? restante.toFixed(2) : '' }])}>+ Agregar instrucción</button>

        {excede && <p role="alert" className="fx-empty" style={{ color: 'var(--danger)' }}>El total asignado supera el efectivo disponible por {toMoney(-restante)}.</p>}
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cancelar</button>
          <button className="fx-btn fx-btn--primary" disabled={busy || excede}>{busy ? 'Guardando…' : 'Guardar instrucciones'}</button></div>
      </form>
    </FondoModal>
  );
}

// Una instrucción: cada tipo muestra solo los campos que necesita.
function LineaInstruccion({ l, numero, sucursalId, sucursales, cuentas, onCuentaCreada, onChange, onQuitar }) {
  const [facturas, setFacturas] = useState([]);
  const [cuentasProv, setCuentasProv] = useState([]);
  const [nueva, setNueva] = useState(null); // formulario de cuenta nueva (empresa o proveedor)
  const set = (campo) => (e) => {
    const v = e.target.value;
    if (campo === 'monto' && !moneyOk(v)) return;
    onChange({ [campo]: v });
  };

  const cargarProv = useCallback(() => {
    if (!l.proveedorId) { setFacturas([]); setCuentasProv([]); return; }
    getPendientesProveedor(l.proveedorId, sucursalId).then(setFacturas).catch(() => setFacturas([]));
    listCuentasProveedor(l.proveedorId).then(setCuentasProv).catch(() => setCuentasProv([]));
  }, [l.proveedorId, sucursalId]);
  useEffect(() => { cargarProv(); }, [cargarProv]);

  const registrarCuenta = async () => {
    if (!nueva.banco.trim() || !nueva.numero.trim()) return Swal.fire('Cuenta bancaria', 'Indica el banco y el número de cuenta.', 'warning');
    try {
      if (nueva.de === 'empresa') {
        if (!nueva.sucursales.length) return Swal.fire('Cuenta bancaria', 'Asocia la cuenta al menos a una sucursal.', 'warning');
        const c = await crearCuentaBancaria({ banco: nueva.banco, numero: nueva.numero, titular: nueva.titular, sucursales: nueva.sucursales });
        await onCuentaCreada();
        onChange({ cuentaBancariaId: c.id });
      } else {
        const c = await crearCuentaProveedor(l.proveedorId, { banco: nueva.banco, numero: nueva.numero, titular: nueva.titular });
        cargarProv();
        onChange({ proveedorCuentaId: c.id });
      }
      setNueva(null);
    } catch (err) { Swal.fire('Cuenta bancaria', err.message || 'No se pudo registrar.', 'error'); }
  };
  const abrirNueva = (de) => setNueva({ de, banco: '', numero: '', titular: '', sucursales: [sucursalId] });
  const etiquetaCuenta = (c) => `${c.banco} · ${c.numero}${c.titular ? ` · ${c.titular}` : ''}`;

  const monto = <label>Monto<MoneyInput value={l.monto} onChange={(v) => onChange({ monto: v })} required /></label>;
  const referencia = (label = 'Referencia / observación') => (
    <label className="full">{label}<input value={l.referencia} onChange={set('referencia')} maxLength={300} /></label>);

  return (
    <div className="fx-card" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <b style={{ fontSize: 14 }}>Instrucción {numero}{l.estado === 'No realizada' && <> <span className="fx-badge fx-badge--vencido">No realizada</span>
          <small style={{ display: 'block', fontWeight: 400, color: 'var(--ink-muted)' }}>{l.noRealizadoMotivo} · Modifícala y guarda para volver a enviarla.</small></>}</b>
        <button type="button" className="fx-link" style={{ color: 'var(--danger)' }} onClick={onQuitar}>Eliminar</button>
      </div>
      <div className="fx-form">
        <label className="full">Tipo<select value={l.tipo} onChange={(e) => { setNueva(null); onChange(camposDeTipo(l, e.target.value)); }}>
          {TIPOS_INSTRUCCION.map(([id, t]) => <option key={id} value={id}>{t}</option>)}</select></label>

        {l.tipo === 'depositar' && <>
          {monto}
          <label>Cuenta bancaria
            <select value={l.cuentaBancariaId || ''} onChange={(e) => (e.target.value === '+' ? abrirNueva('empresa') : onChange({ cuentaBancariaId: e.target.value || null }))}>
              <option value="">— Selecciona —</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{etiquetaCuenta(c)}</option>)}
              <option value="+">+ Registrar cuenta nueva…</option>
            </select>
            {!cuentas.length && <small>No hay cuentas registradas para esta sucursal.</small>}
          </label>
          {referencia()}
        </>}

        {l.tipo === 'proveedor' && <>
          <label className="full">Proveedor
            <ProveedorAutocomplete soloCatalogo value={{ id: l.proveedorId, nombre: l.destino }}
              onChange={(p) => { setNueva(null); onChange({ proveedorId: p.id, destino: p.nombre, factura: '', cuentaPorPagarId: null, proveedorCuentaId: null }); }} /></label>
          {l.proveedorId && <>
            <label>Factura
              <select value={l.cuentaPorPagarId || ''} onChange={(e) => {
                const f = facturas.find((x) => x.id === e.target.value);
                onChange({ cuentaPorPagarId: f?.id || null, factura: f ? (f.documento || '') : '', ...(f && !n(l.monto) ? { monto: f.saldo.toFixed(2) } : {}) });
              }}>
                <option value="">Otra / sin factura registrada</option>
                {facturas.map((f) => <option key={f.id} value={f.id}>{f.documento || 'Sin ref.'} · saldo {toMoney(f.saldo)}</option>)}
              </select>
              {!l.cuentaPorPagarId && <input value={l.factura} onChange={set('factura')} maxLength={80} placeholder="No. de factura (opcional)" style={{ marginTop: 4 }} />}
            </label>
            {monto}
            <label className="full">Cuenta bancaria del proveedor <small>(si aplica)</small>
              <select value={l.proveedorCuentaId || ''} onChange={(e) => (e.target.value === '+' ? abrirNueva('proveedor') : onChange({ proveedorCuentaId: e.target.value || null }))}>
                <option value="">No aplica (pago en efectivo)</option>
                {cuentasProv.map((c) => <option key={c.id} value={c.id}>{etiquetaCuenta(c)}</option>)}
                <option value="+">+ Registrar cuenta del proveedor…</option>
              </select>
            </label>
          </>}
          {referencia()}
        </>}

        {l.tipo === 'servicio' && <>
          <label className="full">Servicio / beneficiario<input value={l.destino} onChange={set('destino')} maxLength={160} required placeholder="Ej.: EEGSA, agua, internet" /></label>
          {monto}
          <label>Referencia<input value={l.referencia} onChange={set('referencia')} maxLength={300} placeholder="No. de contador, contrato…" /></label>
          <label className="full" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            <input type="checkbox" checked={l.conCuenta} onChange={(e) => onChange({ conCuenta: e.target.checked, ...(e.target.checked ? {} : { banco: '', cuenta: '' }) })} />
            Se paga a una cuenta bancaria</label>
          {l.conCuenta && <>
            <label>Banco<input value={l.banco} onChange={set('banco')} maxLength={80} /></label>
            <label>Cuenta bancaria<input value={l.cuenta} onChange={set('cuenta')} maxLength={60} /></label>
          </>}
        </>}

        {l.tipo === 'propietario' && <>
          <label>Nombre de la persona<input value={l.destino} onChange={set('destino')} maxLength={160} required placeholder="Quién recibe el efectivo" /></label>
          {monto}
          {referencia('Observación (opcional)')}
        </>}

        {l.tipo === 'otro' && <>
          <label className="full">Destino / descripción<input value={l.destino} onChange={set('destino')} maxLength={160} required placeholder="A quién o para qué" /></label>
          {monto}
          {referencia()}
        </>}
      </div>

      {nueva && (
        <div className="fx-card" style={{ marginTop: 10, background: 'var(--surface-soft)' }}>
          <b style={{ fontSize: 13 }}>{nueva.de === 'empresa' ? 'Nueva cuenta bancaria de la empresa' : `Nueva cuenta de ${l.destino}`}</b>
          <div className="fx-form" style={{ marginTop: 6 }}>
            <label>Banco<input value={nueva.banco} onChange={(e) => setNueva({ ...nueva, banco: e.target.value })} maxLength={80} /></label>
            <label>Número de cuenta<input value={nueva.numero} onChange={(e) => setNueva({ ...nueva, numero: e.target.value })} maxLength={60} /></label>
            <label className="full">Titular <small>(opcional)</small><input value={nueva.titular} onChange={(e) => setNueva({ ...nueva, titular: e.target.value })} maxLength={120} /></label>
            {nueva.de === 'empresa' && (
              <fieldset className="full" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend style={{ fontSize: 13, fontWeight: 600 }}>Sucursales que usan esta cuenta</legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                  {sucursales.map((s) => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
                      <input type="checkbox" checked={nueva.sucursales.includes(s.id)}
                        onChange={(e) => setNueva({ ...nueva, sucursales: e.target.checked ? [...nueva.sucursales, s.id] : nueva.sucursales.filter((x) => x !== s.id) })} />
                      {s.nombre}</label>))}
                </div>
              </fieldset>)}
          </div>
          <div className="fx-actions" style={{ marginTop: 8 }}>
            <button type="button" className="fx-btn" onClick={() => setNueva(null)}>Cancelar</button>
            <button type="button" className="fx-btn fx-btn--primary" onClick={registrarCuenta}>Registrar cuenta</button>
          </div>
        </div>)}
    </div>
  );
}
